import React, { createContext, useContext, useState, useEffect } from 'react';
import { supportAuthAPI, LoginRequest, SupportUserResponse } from '../lib/api';
import { identifyAnalyticsUser, resetAnalyticsUser } from '../lib/analytics';
import { cabinetPath, currentCabinetRole } from '../lib/domains';

interface SupportAuthContextType {
    user: SupportUserResponse | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (data: LoginRequest) => Promise<void>;
    logout: () => void;
}

const SupportAuthContext = createContext<SupportAuthContextType | undefined>(undefined);

export const SupportAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<SupportUserResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    useEffect(() => {
        if (currentCabinetRole() === 'merchant') {
            setIsLoading(false);
            return;
        }
        const checkAuth = async () => {
            const token = localStorage.getItem('support_access_token');

            if (token) {
                try {
                    const userData = await supportAuthAPI.getCurrentUser();
                    setUser(userData);
                    setIsAuthenticated(true);
                    identifyAnalyticsUser('support', userData);
                } catch (error) {
                    console.error('Failed to fetch support user:', error);
                    logout();
                }
            }
            setIsLoading(false);
        };

        checkAuth();
    }, []);

    const login = async (data: LoginRequest) => {
        setIsLoading(true);
        try {
            const response = await supportAuthAPI.login(data);
            localStorage.setItem('support_access_token', response.access_token);

            const userData = await supportAuthAPI.getCurrentUser();
            setUser(userData);
            setIsAuthenticated(true);
            identifyAnalyticsUser('support', userData);
        } catch (error) {
            console.error('Login failed:', error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const logout = () => {
        supportAuthAPI.logout();
        setUser(null);
        setIsAuthenticated(false);
        resetAnalyticsUser();
        window.location.href = cabinetPath('support', '/login');
    };

    return (
        <SupportAuthContext.Provider value={{ user, isAuthenticated, isLoading, login, logout }}>
            {children}
        </SupportAuthContext.Provider>
    );
};

export const useSupportAuth = () => {
    const context = useContext(SupportAuthContext);
    if (context === undefined) {
        throw new Error('useSupportAuth must be used within a SupportAuthProvider');
    }
    return context;
};
